/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-05-23 13:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { connect } from 'react-redux';
import React from 'react';
import { Modal } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field, reduxForm, reset, change, getFormValues } from 'redux-form';
import { prettyText } from 'src/helpers';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import * as supportTicketActions from 'actions/support-ticket-actions';
import { isRequired, isEmail, isExceedLength } from 'src/helpers/form-validation-helper';

const isRequiredValueEmail = isRequired('Working email is required');
const isRequiredValuePhone = isRequired('Phone number is required');
const isEmailValue = isEmail();
const isExceedLengthValue = isExceedLength(10000);

type Props = {
  active: boolean,
  sending: boolean,
  formValues: Object,
  handleCancel: () => void,
  updateField: () => void,
  handleSubmit: () => void,
  sendSupportTicket: () => void
};

type ComposedProps = Props & WithI18nProps;
// DONE
const EnterpriseModal = (props: ComposedProps) => {
  const {
    active,
    sending = false,
    formValues = {},
    handleSubmit,
    updateField,
    sendSupportTicket,
    handleCancel,
    i18n
  } = props;
  const { email = '', phone = '' } = formValues;
  const checkList = ['Filter', 'Search', 'Recommendation', 'Merchandising'];

  const selectOptions = [
    {
      value: 'Fashion&Apparel&Accessories',
      label: i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Option01')
    },
    {
      value: 'ConsumerElectronics&Gadgets',
      label: i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Option02')
    },
    {
      value: 'Home&Garden',
      label: i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Option03')
    },
    {
      value: 'Toys&Hobbies',
      label: i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Option04')
    },
    {
      value: 'Food&Drinks',
      label: i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Option05')
    },
    {
      value: 'Others',
      label: i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Option06')
    }
  ];

  // Close modal
  const closeModal = () => {
    handleCancel();
  };

  const sendRequest = async data => {
    const features = checkList.filter((feature, index) => {
      return data.features[index];
    });
    const prettyData = {
      email: prettyText(data.email),
      phone: prettyText(data.phone),
      content: prettyText(data.content),
      selection: {
        features,
        industry: data.industry,
        traffic: data.traffic
      }
    };
    const result = await sendSupportTicket(prettyData);

    if (result.type === 'SEND_SUPPORT_TICKET_SUCCESS') {
      handleCancel();
    }
  };

  return (
    <Modal
      open={active}
      onClose={closeModal}
      title={i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.title')}
      primaryAction={{
        content: i18n.translate('AdminHTML.Common.Action.sendRequest'),
        loading: sending,
        disabled: _.isEmpty(email) || _.isEmpty(phone),
        onAction: handleSubmit(sendRequest.bind(this))
      }}
      secondaryActions={[
        {
          content: i18n.translate('AdminHTML.Common.Action.cancel'),
          onAction: closeModal
        }
      ]}>
      <Modal.Section>
        <div className="pfs-enterprise-request-modal">
          <p>{i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.detail')}</p>
          <div className="row row-2">
            <Field
              name="email"
              label={i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Label01')}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Placeholder01'
              )}
              component={RenderTextField}
              validate={[isRequiredValueEmail, isEmailValue]}
            />
            <Field
              name="phone"
              label={i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Label02')}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Placeholder02'
              )}
              component={RenderTextField}
              validate={[isRequiredValuePhone]}
            />
          </div>
          <div className="row row-2">
            <Field
              name="industry"
              label={i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Label')}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.EnterprisePlanModal.select_Placeholder'
              )}
              component={RenderSelect}
              options={selectOptions}
            />
            <Field
              name="traffic"
              label={i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Label03')}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Placeholder03'
              )}
              component={RenderTextField}
            />
          </div>
          <div className="row">
            <p>{i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.checkbox_label')}</p>
            <div className="checkbox-list">
              {checkList.map((element, index) => (
                <Field
                  name={`features[${index}]`}
                  component={RenderCheckbox}
                  onChange={e => updateField(`features[${index}]`, element)}
                  value={element}
                  label={i18n.translate(
                    `AdminHTML.SubscriptionV4.EnterprisePlanModal.checkbox_Content0${index + 1}`
                  )}
                />
              ))}
            </div>
          </div>
          <div className="row">
            <Field
              name="content"
              label={i18n.translate('AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Label04')}
              component={RenderTextField}
              anotherProps={{ multiline: 4, autoComplete: 'off' }}
              placeholder={i18n.translate(
                'AdminHTML.SubscriptionV4.EnterprisePlanModal.input_Placeholder04'
              )}
              validate={[isExceedLengthValue]}
            />
          </div>
        </div>
      </Modal.Section>
    </Modal>
  );
};

const mapStateToProps = (state, props) => {
  const { products } = props;
  const { supportTicket } = state;
  const formValues = getFormValues('EnterpriseModal')(state);
  return {
    initialValues: {
      email: '',
      content: ''
    },
    products,
    formValues,
    sendRequestLoading: _.get(supportTicket, 'sendSupportTicketLoading', false),
    sendRequestSuccess: _.get(supportTicket, 'sendSupportTicketSuccess', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendSupportTicket: data =>
    dispatch(supportTicketActions.sendSupportTicket(data, [], ownProps.i18n)),
  resetSendSupportTicket: () => dispatch(supportTicketActions.resetSendSupportTicket()),
  updateField: (id, data) => dispatch(change(`DefaultThemeSettingsForm`, id, data)),
  resetForm: () => dispatch(reset('EnterpriseModal'))
});

const EnterpriseModalForm = reduxForm({
  form: 'EnterpriseModal', // a unique identifier for this form
  enableReinitialize: true
})(EnterpriseModal);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(EnterpriseModalForm));
